import { useCallback } from "react";
import { useFetchData } from "../../../../../../lib/hooks/useDefaultFetchData";
import { useFetchDataCount } from "../../../../../../lib/hooks/useDefaultFetchDataCount";
import { useTableColumns } from "./hooks/useTableColumns";
import { Card, Row, Table } from "antd";
import { TablePagination } from "../../../../../../components/TablePagination";
import { useOnSorterChange } from "./hooks/useOnSorterChange";
import { TRequest } from "./types";
import { IBatchPayment } from "../../../../../../types/BatchPayment";
import BatchPaymentsService from "../../../../../../services/processing/payments";
import { IBatchGet } from "../../../../../../types/Batches";
import ROUTES from "../../../../../../configs/routesConfig";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { useDefualtOnRow } from "../../../../../../lib/hooks/useDefaultOnRow";
import { Header } from "./components/Header";

type TPaymentsTableProps = {
  batchId: string;
  batchData: IBatchGet | null;
  reloadBatch: () => void;
};

export const PaymentsTable = ({
  batchId,
  batchData,
  reloadBatch,
}: TPaymentsTableProps) => {
  const navigate: NavigateFunction = useNavigate();

  const {
    total,
    isLoading: isCountLoading,
    request,
    onPageChange,
    fetchCount,
    setRequest,
  } = useFetchDataCount<{ batchId: string; status?: number; orderBy?: number }>(
    BatchPaymentsService,
    { batchId },
    ["status"],
    50
  );

  const { data, isLoading, fetchData } = useFetchData<TRequest, IBatchPayment>(
    BatchPaymentsService,
    request,
    ".payments-table .ant-table-content"
  );

  const { onSorterChange } = useOnSorterChange(request, setRequest);

  const reloadData = useCallback(() => {
    fetchCount();
    fetchData();
  }, [fetchCount, fetchData]);

  const getPaymentRoute = useCallback(
    (id: string): string => {
      return `${ROUTES.PROCESSING}/${batchId}/payments/${id}?batchNumber=${batchData?.batchNumber}&type=${batchData?.type}`;
    },
    [batchData?.batchNumber, batchData?.type, batchId]
  );

  const navigateToPeyment = useCallback(
    (id: string) => {
      navigate(getPaymentRoute(id));
    },
    [getPaymentRoute, navigate]
  );

  const { onRow } = useDefualtOnRow(navigateToPeyment);

  const { columns } = useTableColumns({
    reloadData,
    reloadBatch,
    getPaymentRoute,
    navigateToPeyment,
  });

  return (
    <Row className="w-100 h-100 py-6 px-8 card-table">
      <Card className="w-100 h-100">
        <Header
          batchId={batchId}
          request={request}
          reloadBatch={reloadBatch}
          setRequest={setRequest}
        />
        <Row className="w-100 pt-6 d-block card-table__body">
          <Table
            dataSource={data}
            columns={columns}
            loading={isLoading}
            rowKey="id"
            pagination={false}
            className="w-100 h-100 small-table clickable-rows-table payments-table"
            onChange={onSorterChange}
            showSorterTooltip={false}
            onRow={onRow}
          />
          {total > 0 && (
            <TablePagination
              isTotalLoading={isCountLoading}
              total={total}
              currentPage={request.pageNumber}
              pageSize={request.pageSize}
              onPageChange={onPageChange}
            />
          )}
        </Row>
      </Card>
    </Row>
  );
};
